import React, { useState } from 'react';
import inventoryServices from '../../inventory/inventoryServices';

import { 
    Dialog, 
    DialogContent, 
    DialogTitle, 
    Tabs, 
    Tab, 
    Box, 
    IconButton, 
    CircularProgress, 
    Badge 
} from '@mui/material';
import { Close, ShoppingCart, LocalOffer, History } from '@mui/icons-material';
import moment from 'moment';
import { useAppData } from '../../../../../../context/AppDataContext';

function ProductRecentTransactionsList({ productId }) {
    const [recentTransactionList, setRecentTransactionList] = useState(null);
    const [dialogStatus, setDialogStatus] = useState(false);
    const [loading, setLoadingStatus] = useState(false);
    const [activeTab, setActiveTab] = useState(0); // 0 = Purchase, 1 = Sales

    const getRecentTransactionList = () => {
        setLoadingStatus(true);
        inventoryServices.getTransactionListByProductId(productId)
            .then(res => {
                setRecentTransactionList(res.data);
                setDialogStatus(true);
            })
            .catch(error => {
                console.error("Failed to load transactions", error);
            })
            .finally(() => {
                setLoadingStatus(false);
            });
    };

    const handleClose = () => {
        setDialogStatus(false);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const purchaseList = recentTransactionList?.purchaseHistory || [];
    const salesList = recentTransactionList?.salesHistory || [];

    return (
        <>
            <button 
                type="button"
                className="btn btn-outline-primary d-inline-flex align-items-center gap-1" 
                onClick={getRecentTransactionList}
                disabled={loading}
            >
                {loading ? <CircularProgress size={16} /> : <History fontSize="small" />}
                Show Recent Transactions
            </button>

            <Dialog
                open={dialogStatus}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
            >
                {/* Modal Title Header */}
                <DialogTitle className="d-flex justify-content-between align-items-center pb-2">
                    <span className="fw-bold fs-5">Recent Transactions</span>
                    <IconButton onClick={handleClose} size="small" aria-label="close">
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers className="pt-0">
                    {/* Navigation Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange} 
                            variant="fullWidth"
                            indicatorColor="primary"
                            textColor="primary"
                        >
                            <Tab 
                                icon={<ShoppingCart fontSize="small" />} 
                                iconPosition="start" 
                                label={
                                    <div className="d-flex align-items-center gap-2">
                                        <span className='me-2'>Sales</span>
                                        <Badge badgeContent={salesList.length} color="primary" />
                                    </div>
                                } 
                            />
                            <Tab 
                                icon={<LocalOffer fontSize="small" />} 
                                iconPosition="start" 
                                label={
                                    <div className="d-flex align-items-center gap-2">
                                        <span className='me-2'>Purchases</span>
                                        <Badge badgeContent={purchaseList.length} color="secondary" />
                                    </div>
                                } 
                            />
                        </Tabs>
                    </Box>


                    {/* Tab 1: Sales History Table */}
                    {activeTab === 0 && (
                        <TransactionTable 
                            data={salesList} 
                            emptyMessage="No recent sales records found for this product." 
                        />
                    )}

                    {/* Tab 2: Purchase History Table */}
                    {activeTab === 1 && (
                        <TransactionTable 
                            data={purchaseList} 
                            emptyMessage="No recent purchase records found for this product." 
                        />
                    )}

                    
                </DialogContent>
            </Dialog>
        </>
    );
}

/**
 * Reusable Table Component for Purchase/Sale Lists
 */
function TransactionTable({ data, emptyMessage }) {
    const {companyLocationDD,userDD,  productDD, partyDD,getDDDataById,getMasterLabel,masters} = useAppData();

    if (!data || data.length === 0) {
        return (
            <div className="text-center text-muted py-5">
                <p className="mb-0 fs-6">{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                    <tr>
                        <th scope='col'>By</th>
                        <th scope='col'>Location</th>
                        <th scope='col'>Party</th>
                        <th scope="col">Date</th>
                        <th scope="col" className="text-end">Rate</th>
                        <th scope="col" className="text-end">Qty</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map(t => (
                        <tr key={t.id}>
                            <td>{getDDDataById(userDD,t.userId)?.label}</td>
                            <td>{getDDDataById(companyLocationDD,t.locationId).label}</td>
                            <td>{getDDDataById(partyDD,t.partyId)?.label}</td>
                            <td>{moment(t.receiveDate).format("LL")}</td>
                            <td className="text-end fw-semibold">{t.cost}</td>
                            <td className="text-end">{t.qty}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default ProductRecentTransactionsList;